// Tier2 Submit 2025 semantic bindings and validation rules not encoded in the field declarations.
// Catalog labels/options remain generated; jurisdiction attachment policy remains in the saved profile.
import type { ResolvedStateField } from '../services/state-fields.ts';
import type { StateFieldValue, TierIIDocumentKind } from '../../tri-shared/types/tierii-state-fields.ts';
import counties from './county-options-2025.json' with { type: 'json' };
export const STATE_POLICIES: Record<
  string,
  {
    equipmentContact?: boolean;
    minimumAttachments?: number;
    minimumEmergencyContacts?: number;
    fireDistrict?: boolean;
    triReportingField?: string;
    sdsExplanation?: boolean;
    county?: { label: string; choices: string[] };
  }
> = {
  CT: {
    equipmentContact: true,
    sdsExplanation: true,
    county: { label: 'Reporting county / planning region', choices: counties.counties.CT },
  },
  ME: { equipmentContact: true },
  NH: { equipmentContact: true },
  RI: { equipmentContact: true, minimumAttachments: 3 },
  VT: { equipmentContact: true },
  CO: { fireDistrict: true, triReportingField: 'bool01' },
  NC: { fireDistrict: true },
  IA: { minimumEmergencyContacts: 2 },
};
export const COMPANY_BINDINGS = ['CO:text02', 'CT:text01', 'NC:text02'];
export const OWNER_BINDINGS = ['NM:text01'];
export const EMAIL_BINDINGS = ['AL:text06', 'ID:text01', 'NH:text01'];
export const RMP_BINDINGS = ['AR:text02', 'OK:text02', 'NC:number02'];
const SHIPMENT_GROUP_STATES = ['AK', 'CT', 'GA', 'MA', 'ME', 'NH', 'RI', 'VT'];
const REQUIRED_SHIPMENT_STATES = ['AK', 'GA', 'MA', 'ME', 'RI', 'VT'];
const REQUESTED_SHIPMENT_STATES = [...REQUIRED_SHIPMENT_STATES, 'CO', 'NC', 'FL'];
const NRC_RELEASE_STATES = ['AR', 'OK'];
const RCRA_GENERATOR_ANSWERS = ['Yes; Large Quantity Generator (LQG)', 'Yes; Small Quantity Generator (SQG)'];
const WATER_SYSTEM_FIELDS: Record<string, [string, string]> = {
  CT: ['text06', 'text07'],
  ME: ['text06', 'text07'],
  RI: ['text02', 'text03'],
  VT: ['text05', 'text07'],
};
const COLORADO_FACILITY_DESCRIPTIONS: Record<string, string[]> = {
  'Oil and Gas': ['bool02', 'bool03', 'bool04', 'bool05'],
  Agriculture: ['bool07', 'bool08', 'bool09'],
};
export const present = (value: StateFieldValue | undefined) =>
  value != null && (typeof value !== 'string' || value.trim() !== '');
export const ATTACHMENT_FIELDS: Record<string, Record<string, TierIIDocumentKind>> = {
  CT: { bool01: 'sds', bool02: 'site-plan' },
  NH: { bool01: 'sds' },
  NC: { bool01: 'floor-plan', bool02: 'rcra-plan' },
  VA: { text01: 'certification' },
  OH: { bool05: 'confidential-location', bool07: 'certification' },
};

export function attachmentFieldApplies(state: string, field: string, fields: ResolvedStateField[]) {
  if (state === 'NC' && field === 'bool02')
    return RCRA_GENERATOR_ANSWERS.includes(String(fields.find((f) => f.field === 'text01')?.value));
  return true;
}

interface RuleContext {
  fields: ResolvedStateField[];
  state: string;
  value: (field: string) => StateFieldValue | undefined;
  required: Set<string>;
  issues: string[];
}
type StateRule = (context: RuleContext) => void;

const nrcReleaseRule: StateRule = ({ value, issues }) => {
  if ((present(value('text07')) || present(value('text08'))) && value('text06') !== 'Yes')
    issues.push('NRC dates or case numbers require a Yes spill/release answer.');
};

const FACILITY_RULES: Record<string, StateRule> = {
  ...Object.fromEntries(NRC_RELEASE_STATES.map((state) => [state, nrcReleaseRule])),
  TX: ({ value, required }) => {
    if (value('text05') === 'Yes') required.add('date02');
    if (value('text03') === 'Yes') required.add('date01');
  },
  NC: ({ value, required }) => {
    if (value('text08') === 'Yes') required.add('date01');
    if (RCRA_GENERATOR_ANSWERS.includes(String(value('text01')))) {
      required.add('bool02');
      required.add('text04');
    }
  },
  CO: ({ value, issues }) => {
    const facilityType = String(value('text01'));
    const group = COLORADO_FACILITY_DESCRIPTIONS[facilityType] ?? [];
    if (group.length && !group.some((k) => value(k) === true))
      issues.push('Select at least one description for the facility type.');
    if (facilityType !== 'Oil and Gas' && COLORADO_FACILITY_DESCRIPTIONS['Oil and Gas']!.some((k) => value(k) === true))
      issues.push('Oil and gas descriptions require the Oil and Gas facility type.');
    if (facilityType !== 'Agriculture' && COLORADO_FACILITY_DESCRIPTIONS.Agriculture!.some((k) => value(k) === true))
      issues.push('Agricultural descriptions require the Agriculture facility type.');
  },
  OH: ({ value, issues }) => {
    if (['bool01', 'bool02', 'bool03'].filter((k) => value(k) === true).length > 1)
      issues.push('Choose only one type of filing.');
    if (present(value('text03')) && value('bool04') !== true)
      issues.push('A previous facility name requires Facility name has changed.');
    if (present(value('text08')) && value('bool06') !== true)
      issues.push('A previous owner requires Owner / Operator has changed.');
  },
};

const shipmentGroupRule: StateRule = ({ fields, state, value, required, issues }) => {
  const modes = fields.filter((f) => f.field.startsWith('bool')).map((f) => f.field);
  modes.forEach((k) => required.delete(k));
  if (
    REQUIRED_SHIPMENT_STATES.includes(state) &&
    shipmentQuestionsApply(fields, state) &&
    !modes.some((k) => value(k) === true)
  )
    issues.push('Select at least one mode of shipment.');
  if (value('bool07') === true) required.add('text01');
  else required.delete('text01');
  if (present(value('text01')) && value('bool07') !== true)
    issues.push('An other shipment description requires the Other shipment mode.');
};

const CHEMICAL_RULES: Record<string, StateRule> = {
  ...Object.fromEntries(SHIPMENT_GROUP_STATES.map((state) => [state, shipmentGroupRule])),
  FL: ({ value, issues }) => {
    if (
      value('text05') !== 'Not Shipped' &&
      !['bool01', 'bool02', 'bool03', 'bool04', 'bool05'].some((k) => value(k) === true)
    )
      issues.push('Select at least one mode of shipment.');
  },
  NC: ({ value, required, issues }) => {
    if (value('text03') !== 'Not Shipped' && !['bool01', 'bool02', 'bool03', 'bool04'].some((k) => value(k) === true))
      issues.push('Select at least one mode of shipment.');
    if (value('bool01') === true) required.add('text01');
    if (value('bool02') === true) required.add('text02');
  },
  CO: ({ value, required, issues }) => {
    if (value('bool01') === true) required.add('text01');
    if (value('bool02') === true) required.add('text02');
    if (!['bool01', 'bool02', 'bool03'].some((k) => value(k) === true))
      issues.push('Select at least one delivery method.');
    if (present(value('text01')) && value('bool01') !== true) issues.push('Rail car details require Rail delivery.');
    if (present(value('text02')) && value('bool02') !== true) issues.push('Trailer details require Truck delivery.');
  },
};

function shipmentQuestionsApply(fields: ResolvedStateField[], state: string) {
  return state !== 'MA' || fields.some((field) => field.required);
}

export function stateFieldContext(fields: ResolvedStateField[], state: string, scope: 'facility' | 'chemical') {
  const values = Object.fromEntries(fields.map((f) => [f.field, f.value]));
  const context: RuleContext = {
    fields,
    state,
    value: (field) => values[field],
    required: new Set(fields.filter((f) => f.required).map((f) => f.field)),
    issues: [],
  };
  if (scope === 'facility') {
    const water = WATER_SYSTEM_FIELDS[state];
    if (water && context.value(water[0]) === 'Yes') context.required.add(water[1]);
    FACILITY_RULES[state]?.(context);
  } else CHEMICAL_RULES[state]?.(context);
  // Requested as a group; no individual shipment mode is mandatory.
  const requested = new Set(
    scope === 'chemical' && shipmentQuestionsApply(fields, state) && REQUESTED_SHIPMENT_STATES.includes(state)
      ? fields.filter((field) => field.field.startsWith('bool')).map((field) => field.field)
      : [],
  );
  return { required: context.required, requested, issues: context.issues };
}
